import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAPI } from '../api';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TableSortLabel,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function StatsJoueurs() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderBy, setOrderBy] = useState('matchs');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - STATISTIQUES JOUEURS';
    
    loadPlayerStats();
  }, []);

  const loadPlayerStats = async () => {
    try {
      setLoading(true);
      
      // Récupérer tous les joueurs
      const playersData = await fetchAPI('/players');
      
      // Récupérer toutes les statistiques
      const statsResponse = await fetch(`${API_URL}/player-stats`);
      const statsData = await statsResponse.json();
      
      // Combiner les données
      const playersWithStats = playersData.map(player => {
        const playerStat = statsData.find(s => s.id_player === player.rowid);
        return {
          ...player,
          matchs: playerStat?.matchs || 0,
          buts: playerStat?.buts || 0,
          passes: playerStat?.passes || 0,
          victoires: playerStat?.victoires || 0,
          nuls: playerStat?.nuls || 0,
          defaites: playerStat?.defaites || 0,
          sauvetages: playerStat?.sauvetages || 0
        };
      });
      
      setPlayers(playersWithStats);
    } catch (error) {
      console.error('Erreur chargement stats joueurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedPlayers = [...players].sort((a, b) => {
    if (order === 'asc') {
      return a[orderBy] < b[orderBy] ? -1 : 1;
    }
    return a[orderBy] > b[orderBy] ? -1 : 1;
  });

  if (loading) {
    return (
      <div>
        {/* Header avec bouton retour */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2, 
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{ 
              p: 1,
              minWidth: 'auto',
              width: 'auto'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
          <Box sx={{ width: '48px' }} />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div>
      {/* Header avec bouton retour */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2, 
        bgcolor: '#fff',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <IconButton 
          onClick={() => navigate('/')} 
          sx={{ 
            p: 1,
            minWidth: 'auto',
            width: 'auto'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
        <Box sx={{ width: '48px' }} />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center', py: 1 }}>
          Statistiques par joueur
        </Typography>

        <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', borderRadius: 0 }}>
          <Table sx={{ width: '100%', tableLayout: 'auto' }}>
            <TableHead sx={{ bgcolor: '#211b3f' }}>
              <TableRow>
                <TableCell 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    position: 'sticky',
                    left: 0,
                    bgcolor: '#211b3f',
                    zIndex: 2,
                    width: '25%',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Joueur
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }} align="center">
                  <TableSortLabel
                    active={orderBy === 'matchs'}
                    direction={orderBy === 'matchs' ? order : 'asc'}
                    onClick={() => handleSort('matchs')}
                    sx={{ 
                      color: 'white !important',
                      '&:hover': { color: 'white !important' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    M
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }} align="center">
                  <TableSortLabel
                    active={orderBy === 'buts'}
                    direction={orderBy === 'buts' ? order : 'asc'}
                    onClick={() => handleSort('buts')}
                    sx={{ 
                      color: 'white !important',
                      '&:hover': { color: 'white !important' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    B
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }} align="center">
                  <TableSortLabel
                    active={orderBy === 'passes'}
                    direction={orderBy === 'passes' ? order : 'asc'}
                    onClick={() => handleSort('passes')}
                    sx={{ 
                      color: 'white !important',
                      '&:hover': { color: 'white !important' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    PD
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }} align="center">
                  <TableSortLabel
                    active={orderBy === 'sauvetages'}
                    direction={orderBy === 'sauvetages' ? order : 'asc'}
                    onClick={() => handleSort('sauvetages')}
                    sx={{ 
                      color: 'white !important',
                      '&:hover': { color: 'white !important' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    S
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }} align="center">
                  <TableSortLabel
                    active={orderBy === 'victoires'}
                    direction={orderBy === 'victoires' ? order : 'asc'}
                    onClick={() => handleSort('victoires')}
                    sx={{ 
                      color: 'white !important',
                      '&:hover': { color: 'white !important' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    V
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }} align="center">
                  <TableSortLabel
                    active={orderBy === 'nuls'}
                    direction={orderBy === 'nuls' ? order : 'asc'}
                    onClick={() => handleSort('nuls')}
                    sx={{ 
                      color: 'white !important',
                      '&:hover': { color: 'white !important' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    N
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '12.5%' }} align="center">
                  <TableSortLabel
                    active={orderBy === 'defaites'}
                    direction={orderBy === 'defaites' ? order : 'asc'}
                    onClick={() => handleSort('defaites')}
                    sx={{ 
                      color: 'white !important',
                      '&:hover': { color: 'white !important' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    D
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPlayers.map((player, index) => {
                const bgColor = index % 2 === 0 ? '#f5f5f5' : 'white';
                const fullName = `${player.firstname} ${player.lastname}`;
                const displayName = fullName.length > 12 ? fullName.substring(0, 12) + '...' : fullName;
                return (
                <TableRow 
                  key={player.rowid}
                  hover
                  sx={{ 
                    cursor: 'pointer',
                    '& td': {
                      bgcolor: bgColor,
                    },
                    '&:hover td': { 
                      bgcolor: '#e3f2fd !important'
                    }
                  }}
                  onClick={() => navigate(`/joueur/${player.rowid}`)}
                >
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      width: '25%',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {player.label}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{player.matchs}</TableCell>
                  <TableCell align="center">{player.buts}</TableCell>
                  <TableCell align="center">{player.passes}</TableCell>
                  <TableCell align="center" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                    {player.sauvetages}
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {player.victoires}
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'warning.main' }}>
                    {player.nuls}
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'error.main' }}>
                    {player.defaites}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {players.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Aucune statistique disponible
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  );
}

